import type { AIInsightsAnalysis } from '@hooks/useInsights';
import { ChevronDown, PlusCircle } from 'lucide-react';
import { type ReactElement, useState } from 'react';

interface AIAnalysisSummaryProps {
  aiAnalysis: AIInsightsAnalysis;
}

export function AIAnalysisSummary({ aiAnalysis }: AIAnalysisSummaryProps): ReactElement {
  const [expanded, setExpanded] = useState(false);

  // Map risk profile to styling
  const riskStyles: Record<string, { bg: string; text: string; label: string }> = {
    conservative: {
      bg: 'bg-theme-success/30',
      text: 'text-theme-success',
      label: 'Conservative',
    },
    moderate: {
      bg: 'bg-theme-warning/30',
      text: 'text-theme-warning',
      label: 'Moderate',
    },
    aggressive: {
      bg: 'bg-theme-danger/30',
      text: 'text-theme-danger',
      label: 'Aggressive',
    },
  };
  const riskStyle = riskStyles[aiAnalysis.riskProfile] || riskStyles.moderate;

  return (
    <div className="mb-4 bg-gradient-to-r from-purple-900/20 to-indigo-900/20 rounded-lg border border-purple-800/30">
      {/* Header */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <PlusCircle className="w-5 h-5 text-purple-400" />
            <h3 className="font-semibold text-purple-300">AI Analysis</h3>
            <span className="text-xs text-theme-text-tertiary">
              Personalized insights powered by AI
            </span>
          </div>
          <div
            className={`px-2 py-1 rounded text-xs font-medium ${riskStyle.bg} ${riskStyle.text}`}
          >
            {riskStyle.label} Bettor
          </div>
        </div>

        {/* Overall Assessment */}
        <p className="text-theme-text-secondary text-sm mb-4">{aiAnalysis.overallAssessment}</p>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-3">
          {aiAnalysis.keyStrengths.length > 0 && (
            <div className="bg-theme-surface/50 rounded p-3">
              <h4 className="text-theme-success text-xs font-medium mb-2">Key Strengths</h4>
              <ul className="text-xs text-theme-text-secondary space-y-1">
                {aiAnalysis.keyStrengths.slice(0, 2).map((s, i) => (
                  <li key={i} className="flex items-start gap-1">
                    <span className="text-theme-success mt-0.5">+</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {aiAnalysis.criticalWeaknesses.length > 0 && (
            <div className="bg-theme-surface/50 rounded p-3">
              <h4 className="text-theme-danger text-xs font-medium mb-2">Areas to Improve</h4>
              <ul className="text-xs text-theme-text-secondary space-y-1">
                {aiAnalysis.criticalWeaknesses.slice(0, 2).map((w, i) => (
                  <li key={i} className="flex items-start gap-1">
                    <span className="text-theme-danger mt-0.5">-</span>
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Expandable Section */}
      {(aiAnalysis.personalizedTips.length > 0 || aiAnalysis.improvementPriorities.length > 0) && (
        <>
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="w-full px-4 py-2 flex items-center justify-center gap-2 text-xs text-purple-400 hover:text-purple-300 border-t border-purple-800/30 transition"
          >
            {expanded ? 'Show Less' : 'Show Personalized Tips'}
            <ChevronDown
              className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
            />
          </button>

          {expanded && (
            <div className="px-4 pb-4 border-t border-purple-800/30 pt-3">
              {aiAnalysis.improvementPriorities.length > 0 && (
                <div className="mb-3">
                  <h4 className="text-purple-300 text-xs font-medium mb-2">Priority Focus Areas</h4>
                  <ol className="text-xs text-theme-text-secondary space-y-1 list-decimal list-inside">
                    {aiAnalysis.improvementPriorities.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </ol>
                </div>
              )}
              {aiAnalysis.personalizedTips.length > 0 && (
                <div>
                  <h4 className="text-purple-300 text-xs font-medium mb-2">Personalized Tips</h4>
                  <ul className="text-xs text-theme-text-secondary space-y-1">
                    {aiAnalysis.personalizedTips.map((tip, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-purple-400">*</span>
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Disclaimer */}
      <div className="px-4 pb-3">
        <p className="text-theme-text-tertiary text-xs italic">
          AI analysis is for informational purposes only and should not be considered financial
          advice.
        </p>
      </div>
    </div>
  );
}
